import React from 'react';
import { clsx } from 'clsx';

/**
 * LoadingSpinner - Componente de loading com anéis múltiplos rotativos
 * 
 * @param {string} text - Texto a ser exibido (padrão: 'Carregando...')
 * @param {string} size - Tamanho do spinner: 'xs', 'sm', 'md', 'lg', 'xl', '2xl' (padrão: 'md')
 * @param {string} variant - Variante: 'primary', 'secondary', 'success', 'warning', 'danger', 'white', 'gray' (padrão: 'primary')
 * @param {boolean} fullScreen - Se deve ocupar tela inteira com card estilizado (padrão: false)
 * @param {boolean} overlay - Adiciona overlay escuro de fundo (padrão: false)
 * @param {string} speed - Velocidade da animação: 'slow', 'normal', 'fast' (padrão: 'normal')
 * @param {string} className - Classes CSS adicionais
 * 
 * Exemplos de uso:
 * <LoadingSpinner /> // Básico minimalista
 * <LoadingSpinner text="Processando..." size="lg" variant="success" />
 * <LoadingSpinner fullScreen overlay text="Sincronizando dados..." />
 * <LoadingSpinner variant="white" size="sm" speed="fast" />
 */

const LoadingSpinner = ({ 
  text = 'Carregando...', 
  size = 'md', 
  variant = 'primary',
  fullScreen = false,
  overlay = false,
  speed = 'normal',
  className = '' 
}) => {
  // Mapeamento de compatibilidade com versão anterior
  const normalizeSize = (s) => {
    if (s === 'large') return 'lg';
    if (s === 'small') return 'sm';
    return s;
  };

  const normalizedSize = normalizeSize(size);

  // Definições de tamanhos para os 3 anéis (externo, médio, interno)
  const sizes = {
    xs: { 
      outer: 'w-6 h-6', 
      middle: 'w-5 h-5', 
      inner: 'w-4 h-4',
      border: 'border-2'
    },
    sm: { 
      outer: 'w-8 h-8', 
      middle: 'w-7 h-7', 
      inner: 'w-5 h-5',
      border: 'border-2'
    },
    md: { 
      outer: 'w-12 h-12', 
      middle: 'w-10 h-10', 
      inner: 'w-8 h-8',
      border: 'border-[3px]'
    },
    lg: { 
      outer: 'w-16 h-16', 
      middle: 'w-14 h-14', 
      inner: 'w-11 h-11',
      border: 'border-[3px]'
    },    xl: { 
      outer: 'w-20 h-20', 
      middle: 'w-16 h-16', 
      inner: 'w-12 h-12',
      border: 'border-4'
    },
    '2xl': { 
      outer: 'w-24 h-24', 
      middle: 'w-20 h-20', 
      inner: 'w-16 h-16',
      border: 'border-4'
    }
  };

  // Velocidades de rotação
  const speeds = {
    slow: {
      outer: 'animate-[spin_2s_linear_infinite]',
      middle: 'animate-[spin-reverse_1.5s_linear_infinite]', 
      inner: 'animate-[spin_3s_linear_infinite]'
    },
    normal: {
      outer: 'animate-[spin_1s_linear_infinite]',
      middle: 'animate-[spin-reverse_0.7s_linear_infinite]',
      inner: 'animate-[spin_1.5s_linear_infinite]'
    },
    fast: {
      outer: 'animate-[spin_0.6s_linear_infinite]',
      middle: 'animate-[spin-reverse_0.4s_linear_infinite]',
      inner: 'animate-[spin_0.8s_linear_infinite]'
    }
  };

  // Variantes de cores com sistema de cores do CantinaGo
  const variants = {
    primary: {
      outer: 'border-primary-500 border-t-transparent border-r-transparent',
      middle: 'border-transparent border-b-primary-600 border-l-primary-600',
      inner: 'border-primary-400 border-t-transparent border-r-transparent',
      glow: 'shadow-primary-500/25',
      dots: 'bg-primary-500'
    },
    secondary: {
      outer: 'border-secondary-500 border-t-transparent border-r-transparent',
      middle: 'border-transparent border-b-secondary-600 border-l-secondary-600',
      inner: 'border-secondary-400 border-t-transparent border-r-transparent',
      glow: 'shadow-secondary-500/25',
      dots: 'bg-secondary-500'
    },
    success: {
      outer: 'border-green-500 border-t-transparent border-r-transparent',
      middle: 'border-transparent border-b-green-600 border-l-green-600',
      inner: 'border-green-400 border-t-transparent border-r-transparent',
      glow: 'shadow-green-500/25',
      dots: 'bg-green-500'
    },
    warning: {
      outer: 'border-amber-500 border-t-transparent border-r-transparent',
      middle: 'border-transparent border-b-amber-600 border-l-amber-600',
      inner: 'border-amber-400 border-t-transparent border-r-transparent',
      glow: 'shadow-amber-500/25',
      dots: 'bg-amber-500'
    },
    danger: {
      outer: 'border-red-500 border-t-transparent border-r-transparent',
      middle: 'border-transparent border-b-red-600 border-l-red-600',
      inner: 'border-red-400 border-t-transparent border-r-transparent',
      glow: 'shadow-red-500/25',
      dots: 'bg-red-500'
    },
    white: {
      outer: 'border-white border-t-transparent border-r-transparent',
      middle: 'border-transparent border-b-gray-200 border-l-gray-200',
      inner: 'border-gray-100 border-t-transparent border-r-transparent',
      glow: 'shadow-white/25',
      dots: 'bg-white'
    },
    gray: {
      outer: 'border-gray-500 border-t-transparent border-r-transparent',
      middle: 'border-transparent border-b-gray-600 border-l-gray-600',
      inner: 'border-gray-400 border-t-transparent border-r-transparent',
      glow: 'shadow-gray-500/25',
      dots: 'bg-gray-500'
    }
  };

  const currentVariant = variants[variant];
  const currentSize = sizes[normalizedSize];
  const currentSpeed = speeds[speed];

  const containerClasses = clsx(
    'flex flex-col items-center justify-center gap-4',
    {
      'min-h-screen': fullScreen,
      'py-12': !fullScreen,
      'fixed inset-0 z-50': overlay,
      'bg-black/50 backdrop-blur-sm': overlay,
    },
    className
  );

  // Componente dos anéis múltiplos baseado no CSS fornecido
  const MultiRingSpinner = () => (
    <div className="relative group">
      {/* Efeito de glow */}
      <div className={clsx(
        'absolute inset-0 rounded-full blur-lg opacity-30 group-hover:opacity-50 transition-opacity duration-500',
        `shadow-2xl ${currentVariant.glow}`,
        currentSize.outer
      )}></div>
      
      {/* Anel externo - rotação normal */}
      <div className={clsx(
        'rounded-full relative',
        currentSize.outer,
        currentSize.border,
        currentVariant.outer,
        currentSpeed.outer
      )}>
        
        {/* Anel médio - rotação reversa */}
        <div className={clsx(
          'absolute inset-0 m-auto rounded-full',
          currentSize.middle,
          currentSize.border,
          currentVariant.middle,
          currentSpeed.middle
        )}>
          
          {/* Anel interno - rotação normal mais lenta */}
          <div className={clsx(
            'absolute inset-0 m-auto rounded-full',
            currentSize.inner,
            currentSize.border,
            currentVariant.inner,
            currentSpeed.inner
          )}></div>
        </div>
      </div>
    </div>
  );

  if (fullScreen || overlay) {
    return (
      <div className={containerClasses}>
        <div className={clsx(
          'bg-white/95 dark:bg-gray-900/95 rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 p-8 backdrop-blur-xl',
          'transform hover:scale-105 transition-all duration-500',
          currentVariant.glow
        )}>
          <div className="flex flex-col items-center space-y-8">
            <MultiRingSpinner />
            
            {text && (
              <div className="text-center space-y-3">
                <p className="text-gray-800 dark:text-gray-100 font-bold text-xl mb-2">{text}</p>
                <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">
                  {speed === 'fast' ? 'Quase pronto...' : 'Aguarde um momento...'}
                </p>
                
                {/* Barra de progresso fake animada */}
                <div className="w-48 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div className={clsx(
                    'h-full rounded-full animate-pulse',
                    'bg-gradient-to-r',
                    variant === 'primary' && 'from-primary-400 to-primary-600',
                    variant === 'secondary' && 'from-secondary-400 to-secondary-600',
                    variant === 'success' && 'from-green-400 to-green-600',
                    variant === 'warning' && 'from-amber-400 to-amber-600',
                    variant === 'danger' && 'from-red-400 to-red-600',
                    variant === 'gray' && 'from-gray-400 to-gray-600',
                    'animate-[progress_2s_ease-in-out_infinite]'
                  )}></div>
                </div>
              </div>
            )}
            
            {/* Animação de pontos melhorada */}
            <div className="flex space-x-2">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className={clsx(
                    'w-3 h-3 rounded-full animate-bounce',
                    currentVariant.dots,
                    'shadow-lg'
                  )}
                  style={{ animationDelay: `${i * 0.15}s` }}
                ></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={containerClasses}>
      <MultiRingSpinner />
      {text && (
        <p className="text-gray-600 dark:text-gray-400 font-medium text-center max-w-xs">
          {text}
        </p>
      )}
    </div>
  );
};

export default LoadingSpinner;